/**
 * Cliente de la vista "Lectura de planos" (DWG/DXF → SVG tagueado, Fase 0).
 *
 * El backend ya existe en la rama `feat/plano-a-hp`:
 *  - `generarPlano` encola el job de conversión (202 → status 'pending').
 *  - `getPlano` trae estado + inventario (bloques/capas) + la URL del SVG.
 *  - el SVG se sirve por una ruta del propio API (el plano NO sale de la infra).
 *
 * Los tipos vienen de `@supernova/shared` (contrato compartido con el API).
 */
import { ApiClientError, apiFetch } from './client.js';
import type { AgruparPorObraResponse, LeerObraReferenciaResponse, ListarObrasReferenciaResponse, ObraReferenciaDTO, ObraReferenciaResumen } from '@supernova/shared';
import { MENSAJE_IA_NO_RESPONDE, MENSAJE_MATERIAL_NO_REVISADO } from '@supernova/shared';
import type { MaterialesPorGrupoRequest, MaterialesPorGrupoResponse } from '@supernova/shared';
import type {
  ReemplazarHojaPlanoRequest,
  TrabajoEnRiesgoPlano,
  ClasificarMaterialesIfcRequest,
  ClasificarMaterialesIfcResponse,
  AlturaPropuestaResponse,
  PlanoAltosVanosResponse,
  AsignarPlanoRenglonRequest,
  AsignarPlanoRenglonResponse,
  ClasificarMagnitudResponse,
  GenerarPlanoResponse,
  GenerarPlanoIfcResponse,
  GetPlanoResponse,
  DecisionDeCapaCorregida,
  SetDecisionCapaResponse,
  GetPlanoIfcStatusResponse,
  ListarPlanoRenglonesResponse,
  PlanoAlturaPropuesta,
  PlanoCatalogsResponse,
  PlanoGaleriaItem,
  PlanosGaleriaResponse,
  PlanoComplementario,
  PlanoInstalacionSupuesta,
  PlanoMagnitudConcepto,
  PlanoRenglon,
  FotoGenerador,
  PlanoRolArchivo,
  ProjectCatalog,
  ProponerComplementariosResponse,
  FilaParaRevisarArmado,
  RevisarArmadoResponse,
  ConvertirCantidadRequest,
  ConvertirCantidadResponse,
  ProponerInstalacionesSupuestoResponse,
  ProyectoHpResponse,
  SetPlanoRolResponse,
  SetPlanoEscalaResponse,
  SugerenciaConcepto,
  SugerirConceptosRequest,
  SugerirConceptosResponse,
  EspecificacionesParaGenerarResponse,
  PlanoOpcion,
  ListarPlanoOpcionesResponse,
  CrearPlanoOpcionRequest,
  CrearPlanoOpcionResponse,
  UsarPlanoOpcionResponse,
  EditarPlanoOpcionRequest,
  CrearGrupoOpcionesRequest,
  CrearGrupoOpcionesResponse,
  UsarGrupoOpcionesResponse,
} from '@supernova/shared';

export type {
  PlanoRenglon,
  AsignarPlanoRenglonRequest,
  SugerenciaConcepto,
  PlanoMagnitudConcepto,
  PlanoAlturaPropuesta,
  PlanoComplementario,
  PlanoInstalacionSupuesta,
};

export async function generarPlano(
  projectId: string,
  fileId: string,
): Promise<GenerarPlanoResponse> {
  return await apiFetch<GenerarPlanoResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/generar`,
    { method: 'POST' },
  );
}

/**
 * Encola la lectura por visión de un PLANO EN PDF (202 → status 'pending'). Un PDF no tiene geometría:
 * sus cantidades son ESTIMACIONES (origen 'pdf_vision'), y la UI lo avisa. El estado se sigue con
 * `getPlano` igual que un DWG.
 */
export async function generarPlanoPdf(
  projectId: string,
  fileId: string,
): Promise<GenerarPlanoResponse> {
  return await apiFetch<GenerarPlanoResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/generar-pdf`,
    { method: 'POST', body: {}, acceptStatus: [202] as number[] },
  );
}

export async function getPlano(
  projectId: string,
  fileId: string,
): Promise<GetPlanoResponse> {
  return await apiFetch<GetPlanoResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}`,
  );
}

/** Guarda (o quita, con null) la corrección de la persona sobre qué es una capa/bloque del plano. */
export async function setDecisionCapa(
  projectId: string,
  fileId: string,
  capa: string,
  decision: DecisionDeCapaCorregida | null,
): Promise<SetDecisionCapaResponse> {
  return await apiFetch<SetDecisionCapaResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/decisiones-capa`,
    { method: 'PUT', body: { capa, decision } },
  );
}

/** Ruta del SVG (fallback si la respuesta no trae `svgUrl`). */
export function planoSvgUrl(projectId: string, fileId: string): string {
  return `/api/v1/projects/${projectId}/planos/${fileId}/svg`;
}

/** Ruta del DXF canónico (lo consume el visor CAD mlightcad). 404 si el plano no lo tiene. */
export function planoDxfUrl(projectId: string, fileId: string): string {
  return `/api/v1/projects/${projectId}/planos/${fileId}/dxf`;
}

/**
 * Descarga el DXF canónico del plano como ArrayBuffer (autenticado, cookie de sesión). Lo consume
 * el visor CAD mlightcad, que parsea y renderiza el DXF en el navegador (el plano NO sale de la
 * infra). No pasa por `apiFetch` porque la respuesta es binaria, no JSON.
 */
export async function fetchPlanoDxf(projectId: string, fileId: string): Promise<ArrayBuffer> {
  let res: Response;
  try {
    res = await fetch(planoDxfUrl(projectId, fileId), {
      credentials: 'include',
      headers: { Accept: 'application/dxf' },
    });
  } catch {
    throw new Error('No se pudo descargar el plano. Revisa tu conexión y vuelve a intentar.');
  }
  if (!res.ok) {
    throw new Error('No se pudo descargar el plano.');
  }
  return await res.arrayBuffer();
}

// ---------------------------------------------------------------------------
// Visor 3D EXPERIMENTAL: DWG/DXF → IFC (solo visualización, NO alimenta la HP)
// ---------------------------------------------------------------------------

/** Encola la conversión del plano CAD a un modelo IFC4 para verlo en 3D (202). Experimental. */
export async function generarPlanoIfc(
  projectId: string,
  fileId: string,
): Promise<GenerarPlanoIfcResponse> {
  return await apiFetch<GenerarPlanoIfcResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/generar-ifc`,
    { method: 'POST', body: {}, acceptStatus: [202] as number[] },
  );
}

/** Estado de la conversión a IFC + URL para abrirlo en el visor 3D (null hasta que esté listo). */
export async function getPlanoIfcStatus(
  projectId: string,
  fileId: string,
): Promise<GetPlanoIfcStatusResponse> {
  return await apiFetch<GetPlanoIfcStatusResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/ifc-status`,
  );
}

/** Ruta del IFC generado (la consume IfcViewer3D vía `srcUrl`, con cookie de sesión). */
export function planoIfcUrl(projectId: string, fileId: string): string {
  return `/api/v1/projects/${projectId}/planos/${fileId}/ifc`;
}

// ---------------------------------------------------------------------------
// HP POR-PROYECTO: la unión de los renglones de TODOS los planos del proyecto
// ---------------------------------------------------------------------------

/**
 * HP UNIFICADA del proyecto: los renglones de TODOS los planos del proyecto (arquitectura, detalles,
 * urbanización) en una sola hoja. Cada renglón trae su archivo fuente (`sourceFileName`), su rol
 * (`planoRol`) y si aporta cantidad al total (`aportaCantidad`, false para láminas de solo referencia).
 */
export async function getPlanosHp(projectId: string): Promise<PlanoRenglon[]> {
  const res = await apiFetch<ProyectoHpResponse>(`/api/v1/projects/${projectId}/planos-hp`);
  return res.renglones;
}

/**
 * Fija el ROL de un archivo/plano dentro de la HP por-proyecto (mide el edificio / mide el sitio /
 * solo referencia). El rol decide si el archivo aporta cantidad o solo enriquece (anti-doble-conteo);
 * cambiarlo se refleja al instante en cada renglón de ese archivo (no toca los renglones).
 */
export async function setPlanoRol(
  projectId: string,
  fileId: string,
  rol: PlanoRolArchivo,
): Promise<SetPlanoRolResponse> {
  return apiFetch<SetPlanoRolResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/rol`,
    { method: 'PUT', body: { rol } },
  );
}

/**
 * Quita UNA nota de lámina pegada a un renglón de planos: ya no vuelve a pegarse sola en ese renglón.
 * Devuelve el renglón actualizado. $0, sin IA.
 */
export async function quitarNotaDeRenglon(
  projectId: string,
  fileId: string,
  renglonId: string,
  nota: { detalleId: string; texto: string },
): Promise<PlanoRenglon> {
  const res = await apiFetch<{ renglon: PlanoRenglon }>(
    `/api/v1/projects/${projectId}/planos/${fileId}/renglones/${renglonId}/notas/quitar`,
    { method: 'POST', body: nota },
  );
  return res.renglon;
}

// ---------------------------------------------------------------------------
// Renglones de la HP armados desde selecciones del plano (Fase 1)
// ---------------------------------------------------------------------------

/**
 * Asigna un concepto del catálogo (elegido a mano) a una selección del plano y
 * arma el renglón de la Hoja de Presupuesto. Re-asignar la MISMA selección
 * reemplaza su renglón en el backend (upsert por tipo+clave).
 */
export async function asignarPlanoRenglon(
  projectId: string,
  fileId: string,
  body: AsignarPlanoRenglonRequest,
): Promise<PlanoRenglon> {
  const res = await sinFotoSiNoCuadra(body, (b) =>
    apiFetch<AsignarPlanoRenglonResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/renglones`, {
      method: 'POST',
      body: b,
      acceptStatus: [201] as number[],
    }),
  );
  return res.renglon;
}

/**
 * Números generadores: si el servidor rechaza la foto de la medición (sus líneas no dan la cantidad), el
 * renglón entra igual, sin foto (su generador dirá que hay que volver a agregarlo). Perder el renglón por
 * su foto sería peor; queda el aviso en la consola para encontrarlo.
 */
async function sinFotoSiNoCuadra<B extends { generador?: unknown; cantidadEscritaAMano?: boolean }, T>(
  body: B,
  enviar: (b: B) => Promise<T>,
): Promise<T> {
  try {
    return await enviar(body);
  } catch (e) {
    if (!(e instanceof ApiClientError) || e.code !== 'GENERADOR_NO_CUADRA' || !body.generador) throw e;
    console.warn('[numeros-generadores] el servidor no aceptó la foto de la medición:', e.message);
    const { generador: _g, cantidadEscritaAMano: _m, ...resto } = body;
    return enviar(resto as B);
  }
}

/**
 * Capa 2: pide a la IA que interprete la entidad seleccionada del plano y
 * sugiera CONCEPTOS equivalentes del catálogo por búsqueda semántica. Se llama
 * al apretar el botón "Sugerir conceptos" (no en cada cambio de selección: cada
 * llamada usa IA). Si la entidad no es cotizable, `sugerencias` viene vacío y
 * `nota` explica por qué.
 */
/** La cantidad de un trabajo cuyo concepto se cobra en otra magnitud que la del plano (ver la ruta). */
export async function convertirCantidadPlano(
  projectId: string,
  fileId: string,
  pedido: ConvertirCantidadRequest,
): Promise<ConvertirCantidadResponse> {
  return await apiFetch<ConvertirCantidadResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/convertir-cantidad`, {
    method: 'POST',
    body: pedido,
  });
}

/** Revisión final de la hoja armada: qué sobra y qué falta (ver la ruta). */
export async function revisarArmadoPlano(
  projectId: string,
  fileId: string,
  filas: FilaParaRevisarArmado[],
  contexto?: string,
): Promise<RevisarArmadoResponse> {
  return await apiFetch<RevisarArmadoResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/revisar-armado`, {
    method: 'POST',
    body: { filas, contexto },
  });
}

export async function sugerirConceptos(
  projectId: string,
  fileId: string,
  body: SugerirConceptosRequest,
): Promise<SugerirConceptosResponse> {
  return await apiFetch<SugerirConceptosResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/sugerencias`,
    { method: 'POST', body },
  );
}

/**
 * Las notas de las láminas «Solo referencia» del proyecto que se le pegan a `texto` (el concepto que se
 * está eligiendo; las MISMAS que se pegan al agregarlo) y las que lo contradicen. $0, sin IA. `nombre`
 * = capa o bloque seleccionado (se descarta al comparar: es jerga de dibujo).
 */
export async function especificacionesParaGenerar(
  projectId: string,
  texto: string,
  nombre?: string | null,
  signal?: AbortSignal,
): Promise<EspecificacionesParaGenerarResponse> {
  const qs = new URLSearchParams({ texto: texto.slice(0, 4000) });
  if (nombre) qs.set('nombre', nombre.slice(0, 200));
  return apiFetch<EspecificacionesParaGenerarResponse>(
    `/api/v1/projects/${projectId}/planos-especificaciones-para-generar?${qs.toString()}`,
    { signal },
  );
}

/**
 * Fase 2 · B) La IA clasifica qué magnitud necesita un concepto del catálogo
 * (conteo/lineal/área/volumen/peso) para saber cómo cuantificarlo desde el plano.
 * Devuelve `magnitud: null` si la IA no pudo decidir (→ el usuario elige a mano).
 */
export async function clasificarMagnitud(
  projectId: string,
  fileId: string,
  catalogId: string,
  resourceId: string,
): Promise<PlanoMagnitudConcepto | null> {
  const res = await apiFetch<ClasificarMagnitudResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/clasificar-magnitud`,
    { method: 'POST', body: { catalogId, resourceId } },
  );
  return res.magnitud;
}

/**
 * Fase 2 · A) La IA propone la altura de muro/entrepiso leyéndola del propio plano
 * (atributos tipo NIVELPLAFON). Se llama una vez por plano. `altura: null` si el
 * plano no permite deducirla (→ el usuario la pone a mano).
 */
export async function alturaPropuesta(
  projectId: string,
  fileId: string,
): Promise<PlanoAlturaPropuesta | null> {
  const res = await apiFetch<AlturaPropuestaResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/altura-propuesta`,
    { method: 'POST' },
  );
  return res.altura;
}

/**
 * Fase 3 · La IA propone los conceptos COMPLEMENTARIOS / previos que un concepto ya
 * asociado implica (un muro → excavación, dala, cadena, castillos…). Devuelve la lista
 * de necesidades; cada una se resuelve luego contra el catálogo. Lista vacía si la IA
 * no vio previos o no está disponible.
 */
export async function proponerComplementarios(
  projectId: string,
  fileId: string,
  catalogId: string,
  resourceId: string,
  seleccionTipo: 'bloque' | 'capa',
  seleccionClave: string,
  yaPropuestas?: string[],
  /** Lo que el modelo dice del elemento y lo que el plano ya trae (ver la ruta). */
  contexto?: string,
  /** Las salidas se cuentan aparte (las propone «lo que el plano no dibuja»). */
  salidasAparte?: boolean,
  /** Caras ya decididas del elemento: mandan sobre las que diga el pedido. */
  caras?: string[],
  /** «Otra opción»: ofrecer «por VANOS» (lo que va en cada puerta o ventana del elemento). */
  porVanos?: boolean,
): Promise<PlanoComplementario[]> {
  const res = await apiFetch<ProponerComplementariosResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/complementarios`,
    { method: 'POST', body: { catalogId, resourceId, seleccionTipo, seleccionClave, yaPropuestas, contexto, salidasAparte, caras, ...(porVanos ? { porVanos } : {}) } },
  );
  return res.complementarios;
}

/**
 * Igual que `proponerComplementarios` pero para un principal SINTÉTICO (hecho por IA, sin recurso
 * de catálogo): la IA propone los adicionales desde la DESCRIPCIÓN del concepto. Es lo que pidió
 * Sebastián: cuando el original se hace por IA, sus conceptos adicionales también.
 */
export async function proponerComplementariosDeSintetico(
  projectId: string,
  fileId: string,
  conceptoDescripcion: string,
  conceptoUnidad: string | null,
  seleccionTipo: 'bloque' | 'capa',
  seleccionClave: string,
  yaPropuestas?: string[],
  contexto?: string,
  salidasAparte?: boolean,
  porVanos?: boolean,
): Promise<PlanoComplementario[]> {
  const res = await apiFetch<ProponerComplementariosResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/complementarios`,
    { method: 'POST', body: { conceptoDescripcion, conceptoUnidad: conceptoUnidad ?? undefined, seleccionTipo, seleccionClave, yaPropuestas, contexto, salidasAparte, ...(porVanos ? { porVanos } : {}) } },
  );
  return res.complementarios;
}

/**
 * Frente #5·B: la IA propone las INSTALACIONES que el plano NO dibuja pero los recintos implican
 * (eléctrica, hidráulica, sanitaria, gas, HVAC…). Las infiere de los nombres de recinto del plano y
 * NO propone la disciplina que ya está dibujada. Lista vacía si no hay recintos o la IA no está.
 */
export async function proponerInstalacionesSupuesto(
  projectId: string,
  fileId: string,
  yaPropuestas?: string[],
  /** Lo que el plano ya trae como elemento propio, con su medida: no se repite y da medidas para estimar. */
  yaTrae?: string[],
): Promise<PlanoInstalacionSupuesta[]> {
  const res = await apiFetch<ProponerInstalacionesSupuestoResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/instalaciones-supuesto`,
    { method: 'POST', body: { yaPropuestas, yaTrae } },
  );
  return res.instalaciones;
}

// ---------------------------------------------------------------------------
// Catálogos de /planos (selección PROPIA, independiente de la cotización)
// ---------------------------------------------------------------------------

/** Catálogos elegidos para /planos, en orden de prioridad (índice 0 = mayor). */
export async function getPlanoCatalogs(projectId: string): Promise<ProjectCatalog[]> {
  const res = await apiFetch<PlanoCatalogsResponse>(
    `/api/v1/projects/${projectId}/plano-catalogs`,
  );
  return res.planoCatalogs;
}

/** Reemplaza la selección de catálogos de /planos. `catalogIds` en orden de prioridad. */
export async function setPlanoCatalogs(
  projectId: string,
  catalogIds: string[],
): Promise<ProjectCatalog[]> {
  const res = await apiFetch<PlanoCatalogsResponse>(
    `/api/v1/projects/${projectId}/plano-catalogs`,
    { method: 'PUT', body: { catalogIds } },
  );
  return res.planoCatalogs;
}

/**
 * Cotizar con IA un renglón cuyo concepto NO está en catálogo: encola la generación del APU
 * sintético (202). El renglón queda `apu_ia_status='queued'`; el front refresca hasta 'done'/'failed'.
 * 429 si no alcanza la bolsa de tokens.
 */
export async function cotizarRenglonConIa(
  projectId: string,
  fileId: string,
  renglonId: string,
): Promise<{ renglonId: string; status: string; jobId: string | null }> {
  return await apiFetch(
    `/api/v1/projects/${projectId}/planos/${fileId}/renglones/${renglonId}/cotizar-ia`,
    { method: 'POST', acceptStatus: [202] as number[] },
  );
}

/**
 * Agrega a la HP un concepto SIN catálogo, por su descripción (no se encontró match). Entra
 * marcado para IA; el lote lo cotiza. Sirve para principales y complementarios no encontrados.
 */
export async function agregarRenglonPorDescripcion(
  projectId: string,
  fileId: string,
  body: {
    descripcion: string;
    unidad?: string;
    cantidad: number;
    rol?: 'principal' | 'complemento';
    partida?: string;
    /** Nivel/zona del modelo BIM (IFC), para los ejes de agrupación etapa 2. */
    nivel?: string | null;
    ubicacion?: string | null;
    /** true = la cantidad descansa en un supuesto de la IA → marcar el renglón para revisar. */
    revisar?: boolean;
    /** Complementario: el renglón principal del que cuelga. */
    principalId?: string | null;
    seleccion?: { tipo: 'bloque' | 'capa'; clave: string; ids?: string[] };
    /** Números generadores: la foto de la medición; los otros principales; si la cantidad se escribió a mano. */
    generador?: FotoGenerador;
    padres?: string[];
    cantidadEscritaAMano?: boolean;
  },
): Promise<PlanoRenglon> {
  const res = await sinFotoSiNoCuadra(body, (b) =>
    apiFetch<{ renglon: PlanoRenglon }>(`/api/v1/projects/${projectId}/planos/${fileId}/renglones/por-descripcion`, {
      method: 'POST',
      body: b,
      acceptStatus: [201] as number[],
    }),
  );
  return res.renglon;
}

/**
 * Corrige la descripción (y unidad, opcional) de un renglón sintético ANTES de generarlo. A la HP
 * entra el nombre del elemento del CAD; con esto el usuario lo ajusta antes de cotizar con IA. Solo
 * funciona mientras el renglón no tenga precio.
 */
export async function editarDescripcionRenglon(
  projectId: string,
  fileId: string,
  renglonId: string,
  body: { descripcion: string; unidad?: string },
): Promise<PlanoRenglon> {
  const res = await apiFetch<{ renglon: PlanoRenglon }>(
    `/api/v1/projects/${projectId}/planos/${fileId}/renglones/${renglonId}/descripcion`,
    { method: 'PATCH', body },
  );
  return res.renglon;
}

/** Subtotales + validaciones + razón del semáforo que el worker guarda para mostrar el análisis. */
export interface PlanoApuDesglose {
  subMateriales?: number; subMo?: number; subEquipo?: number; subHerramienta?: number;
  srCuadrilla?: number; costoDirecto?: number; importe?: number;
  validaciones?: Array<{ id: string; tipo: 'success' | 'warning' | 'danger'; msg: string; critica?: boolean }>;
  confianzaRazon?: string | null;
}

/** Análisis con IA (APU) de un renglón sintético ya generado, para verlo read-only. La matriz y el
 *  desglose vienen crudos (jsonb); la vista los interpreta. 404 si el renglón no tiene análisis. */
/** Detiene el lote de «Generar con IA» del plano: lo que estaba en cola vuelve a pendiente. */
export async function detenerIaLote(
  projectId: string,
  fileId: string,
): Promise<{ detenidos: number; generandose: number }> {
  return await apiFetch(`/api/v1/projects/${projectId}/planos/${fileId}/detener-ia-lote`, { method: 'POST', body: {} });
}

/** Corrige a mano la cantidad de un renglón (la medida del plano queda guardada aparte). */
export async function corregirCantidadRenglon(
  projectId: string,
  fileId: string,
  renglonId: string,
  cantidad: number,
): Promise<PlanoRenglon> {
  const res = await apiFetch<{ renglon: PlanoRenglon }>(
    `/api/v1/projects/${projectId}/planos/${fileId}/renglones/${renglonId}/cantidad`,
    { method: 'PATCH', body: { cantidad } },
  );
  return res.renglon;
}

/** Cambia el concepto de un renglón por otro del catálogo; conserva cantidad, partida y selección. */
export async function cambiarConceptoRenglon(
  projectId: string,
  fileId: string,
  renglonId: string,
  concepto: { catalogId: string; resourceClave: string },
): Promise<{ renglon: PlanoRenglon; unidadCambio: { antes: string | null; despues: string | null } | null }> {
  return await apiFetch(
    `/api/v1/projects/${projectId}/planos/${fileId}/renglones/${renglonId}/cambiar-concepto`,
    { method: 'POST', body: concepto },
  );
}

export async function getPlanoRenglonApu(
  projectId: string,
  fileId: string,
  renglonId: string,
): Promise<{
  matrix: unknown;
  desglose: PlanoApuDesglose | null;
  costoDirecto: number | null;
  confianza: string | null;
  descripcion: string;
  unidad: string | null;
  rendimientoBase: number | null;
  /** Con los factores de obra aplicados (condiciones del proyecto). */
  rendimientoAjustado: number | null;
  /** 'pending' (sin revisar) | 'approved' | 'rejected'. */
  reviewStatus: string | null;
}> {
  const res = await apiFetch<{ apu: {
    matrix: unknown;
    desglose: PlanoApuDesglose | null;
    costoDirecto: number | null; confianza: string | null; descripcion: string; unidad: string | null;
    rendimientoBase: number | null; rendimientoAjustado: number | null; reviewStatus: string | null;
  } }>(
    `/api/v1/projects/${projectId}/planos/${fileId}/renglones/${renglonId}/apu`,
  );
  return res.apu;
}

/** Procedimiento constructivo (documento imprimible) de un renglón sintético — MISMO documento que
 *  cotización. Staff-only. Si no está en caché o el APU cambió, el backend encola y responde
 *  'running'; la UI hace polling hasta 'completed'. */
export async function getPlanoRenglonProcedimiento(
  projectId: string,
  fileId: string,
  renglonId: string,
  variante: import('@supernova/shared').ProcedimientoVariante,
): Promise<import('@supernova/shared').ProcedimientoConstructivo> {
  return await apiFetch(
    `/api/v1/projects/${projectId}/planos/${fileId}/renglones/${renglonId}/procedimiento?variante=${variante}`,
  );
}

/** Prende/apaga el toggle "generar con IA" de un renglón. */
export async function iaToggleRenglon(
  projectId: string,
  fileId: string,
  renglonId: string,
  requested: boolean,
): Promise<void> {
  await apiFetch(
    `/api/v1/projects/${projectId}/planos/${fileId}/renglones/${renglonId}/ia-toggle`,
    { method: 'PATCH', body: { requested }, acceptStatus: [204] as number[] },
  );
}

/** «Marcar todos con IA» / «Quitar»: el toggle en todos los renglones sin precio del plano. */
export async function iaToggleTodos(
  projectId: string,
  fileId: string,
  requested: boolean,
): Promise<{ cambiados: number }> {
  return await apiFetch(`/api/v1/projects/${projectId}/planos/${fileId}/ia-toggle-todos`, {
    method: 'PATCH',
    body: { requested },
  });
}

/** Genera con IA EN LOTE todos los renglones marcados sin precio. 429 si no alcanza la bolsa. */
export async function cotizarIaLote(
  projectId: string,
  fileId: string,
): Promise<{ encolados: number }> {
  return await apiFetch(
    `/api/v1/projects/${projectId}/planos/${fileId}/cotizar-ia-lote`,
    { method: 'POST', acceptStatus: [202] as number[] },
  );
}

/** Renglones armados de un plano, ya ordenados por partida (backend). */
export async function listarPlanoRenglones(
  projectId: string,
  fileId: string,
): Promise<ListarPlanoRenglonesResponse> {
  // Trae también el aviso del último lote de IA (si se cortó porque la IA no respondía).
  return await apiFetch<ListarPlanoRenglonesResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/renglones`,
  );
}

/**
 * IFC: asegura la fila `project_planos` (padre de los renglones de la HP). Un IFC no pasa por el
 * pipeline SVG, así que no existe hasta que se llama esto. Idempotente. Se llama al abrir el modelo.
 */
export async function ensurePlanoIfc(projectId: string, fileId: string): Promise<void> {
  await apiFetch(`/api/v1/projects/${projectId}/planos/${fileId}/ensure-ifc`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Clasifica con IA los renglones que aún no tienen agrupación (ejes especialidad/fase/sistema/
 * material) y devuelve la HP actualizada. Se llama UNA vez al elegir un eje distinto de "familia";
 * cachea en el servidor y reporta el consumo a eko27.
 */
export async function agruparPlano(
  projectId: string,
  fileId: string,
): Promise<PlanoRenglon[]> {
  const res = await apiFetch<ListarPlanoRenglonesResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/agrupar`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return res.renglones;
}

/** Obras de referencia disponibles (solo su árbol de partidas; prueba staff). */
export async function listarObrasReferencia(): Promise<ObraReferenciaResumen[]> {
  const res = await apiFetch<ListarObrasReferenciaResponse>('/api/v1/obras-referencia');
  return res.obras;
}

/** Borra las partidas de una obra subida desde m1. */
export async function borrarObraReferencia(obraId: string): Promise<void> {
  await apiFetch(`/api/v1/obras-referencia/${obraId}`, { method: 'DELETE', acceptStatus: [204] as number[] });
}

/** Una obra de referencia con todas sus partidas. */
export async function leerObraReferencia(obraId: string): Promise<ObraReferenciaDTO> {
  const res = await apiFetch<LeerObraReferenciaResponse>(`/api/v1/obras-referencia/${obraId}`);
  return res.obra;
}

/**
 * Acomoda los conceptos de la hoja sin partida vigente en las partidas de una obra de referencia. Cacheado en
 * el servidor por obra; solo cobra lo que falta.
 */
export async function agruparPlanoPorObra(
  projectId: string,
  fileId: string,
  obraId: string,
): Promise<AgruparPorObraResponse> {
  return apiFetch<AgruparPorObraResponse>(
    `/api/v1/projects/${projectId}/planos/${fileId}/agrupar-obra`,
    { method: 'POST', body: { obraId } },
  );
}

/** Borra un renglón de la HP del plano. */
export async function borrarPlanoRenglon(
  projectId: string,
  fileId: string,
  renglonId: string,
  /** true = también los complementarios que cuelgan de este principal. */
  conComplementarios = false,
): Promise<void> {
  await apiFetch(
    `/api/v1/projects/${projectId}/planos/${fileId}/renglones/${renglonId}${conComplementarios ? '?conComplementarios=1' : ''}`,
    { method: 'DELETE', acceptStatus: [204] as number[] },
  );
}

/**
 * Trae el SVG como texto para inyectarlo inline (DOM real, clickeable). No pasa
 * por `apiFetch` porque la respuesta es `image/svg+xml`, no JSON: se pide con
 * `fetch` directo (cookie incluida) y se lee `.text()`.
 */
export async function fetchPlanoSvg(url: string): Promise<string> {
  let res: Response;
  try {
    res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'image/svg+xml' },
    });
  } catch {
    throw new Error('No se pudo descargar el plano. Revisa tu conexión y vuelve a intentar.');
  }
  if (!res.ok) {
    throw new Error('No se pudo descargar el plano.');
  }
  return await res.text();
}

// ===== Galería de planos del proyecto (OPUS ci) =====

export type { PlanoGaleriaItem };

/** Fichas de los planos del proyecto (IFC, DWG/DXF, PDF) con estado, datos y vista previa. */
export async function getPlanosGaleria(projectId: string): Promise<PlanoGaleriaItem[]> {
  const r = await apiFetch<PlanosGaleriaResponse>(
    `/api/v1/projects/${projectId}/planos-galeria`,
  );
  return r.planos;
}

/**
 * Guarda en qué unidad está dibujado un plano con escala dudosa (el usuario contesta UNA vez). La
 * `unidad` tiene que ser una de `escala.opciones`; el factor lo pone el servidor.
 */
export async function setPlanoEscala(
  projectId: string,
  fileId: string,
  unidad: string,
): Promise<SetPlanoEscalaResponse> {
  return await apiFetch<SetPlanoEscalaResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/escala`, {
    method: 'PUT',
    body: { unidad },
  });
}

/** Sube la vista previa de un modelo IFC (la capturó el visor 3D). Best-effort: nunca bloquea. */
export async function subirMiniaturaPlano(
  projectId: string,
  fileId: string,
  imagen: Blob,
  /** Línea de datos medida por el visor ("57 tipos · 2 pisos · 245 piezas"). */
  datos: string | null,
): Promise<void> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(imagen);
  });
  await apiFetch<void>(`/api/v1/projects/${projectId}/planos/${fileId}/miniatura`, {
    method: 'PUT',
    body: { imagen: dataUrl, datos },
    acceptStatus: [204] as number[],
  });
}

/** Altos de los vanos de un DWG guardados por tipo (clave = `claveDeTipoDeVano`). */
export async function getAltosVanos(projectId: string, fileId: string): Promise<Record<string, number>> {
  const res = await apiFetch<PlanoAltosVanosResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/altos-vanos`);
  return res.altos;
}

/** Guarda (reemplaza) los altos de los vanos del plano. */
export async function setAltosVanos(
  projectId: string,
  fileId: string,
  altos: Record<string, number>,
): Promise<Record<string, number>> {
  const res = await apiFetch<PlanoAltosVanosResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/altos-vanos`, {
    method: 'PUT',
    body: { altos },
  });
  return res.altos;
}

/**
 * Reconoce por su NOMBRE los materiales que un modelo IFC no tipifica (una consulta por modelo; lo ya
 * reconocido sale de la caché sin pagar). Nunca lanza: si la API o la IA fallan, devuelve lo que haya y
 * el texto único del aviso (`MENSAJE_IA_NO_RESPONDE`), y esas piezas quedan sin pesar.
 */
export async function clasificarMaterialesIfc(
  projectId: string,
  fileId: string,
  nombres: string[],
): Promise<ClasificarMaterialesIfcResponse> {
  try {
    return await apiFetch<ClasificarMaterialesIfcResponse>(
      `/api/v1/projects/${projectId}/planos/${fileId}/materiales-ifc`,
      { method: 'POST', body: { nombres } satisfies ClasificarMaterialesIfcRequest },
    );
  } catch (e) {
    const msg = e instanceof ApiClientError ? (e.body as { message?: unknown } | undefined)?.message : undefined;
    return { materiales: [], avisoIa: typeof msg === 'string' && msg ? msg : MENSAJE_IA_NO_RESPONDE };
  }
}

/**
 * «¿De qué material?» solo donde el catálogo lo distingue (armado IFC): el servidor busca en el catálogo del plano y dice,
 * por grupo, si hay que preguntar y con qué opciones. Nunca lanza: si falla, nada se pregunta y viene el aviso.
 */
export async function pedirMaterialesPorGrupo(
  projectId: string,
  fileId: string,
  body: MaterialesPorGrupoRequest,
): Promise<MaterialesPorGrupoResponse> {
  try {
    return await apiFetch<MaterialesPorGrupoResponse>(
      `/api/v1/projects/${projectId}/planos/${fileId}/materiales-por-grupo`,
      { method: 'POST', body },
    );
  } catch {
    return { grupos: [], avisoIa: MENSAJE_MATERIAL_NO_REVISADO };
  }
}

/** SOLO staff Ecosoft: costo en USD de la IA de este plano desde `desde` (ISO), y cuántas llamadas. */
export async function costoIaDelPlano(
  projectId: string,
  fileId: string,
  desde: string,
): Promise<{ usd: number; llamadas: number }> {
  return await apiFetch(`/api/v1/projects/${projectId}/planos/${fileId}/costo-ia?desde=${encodeURIComponent(desde)}`);
}

// ---------------------------------------------------------------------------------------------------
// «Dos opciones con precio» (idea 7): opciones de un renglón principal. Guardar, usar y descartar son $0;
// la opción se arma con las consultas de siempre (sugerencias, complementarios, magnitud).
// ---------------------------------------------------------------------------------------------------

export async function listarOpcionesPlano(projectId: string, fileId: string): Promise<PlanoOpcion[]> {
  const r = await apiFetch<ListarPlanoOpcionesResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/opciones`);
  return r.opciones;
}

export async function crearOpcionPlano(projectId: string, fileId: string, body: CrearPlanoOpcionRequest): Promise<PlanoOpcion> {
  const r = await apiFetch<CrearPlanoOpcionResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/opciones`, {
    method: 'POST',
    body,
    acceptStatus: [201] as number[],
  });
  return r.opcion;
}

export async function descartarOpcionPlano(projectId: string, fileId: string, opcionId: string): Promise<void> {
  await apiFetch<void>(`/api/v1/projects/${projectId}/planos/${fileId}/opciones/${opcionId}`, {
    method: 'DELETE',
    acceptStatus: [204] as number[],
  });
}

export async function usarOpcionPlano(projectId: string, fileId: string, opcionId: string): Promise<UsarPlanoOpcionResponse> {
  return await apiFetch<UsarPlanoOpcionResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/opciones/${opcionId}/usar`, {
    method: 'POST',
  });
}

export async function editarOpcionPlano(projectId: string, fileId: string, opcionId: string, body: EditarPlanoOpcionRequest): Promise<PlanoOpcion> {
  const r = await apiFetch<CrearPlanoOpcionResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/opciones/${opcionId}`, { method: 'PATCH', body });
  return r.opcion;
}

export async function crearGrupoOpcionesPlano(projectId: string, fileId: string, body: CrearGrupoOpcionesRequest): Promise<CrearGrupoOpcionesResponse> {
  return await apiFetch<CrearGrupoOpcionesResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/opciones/grupos`, {
    method: 'POST',
    body,
    acceptStatus: [201] as number[],
  });
}

export async function usarGrupoOpcionesPlano(projectId: string, fileId: string, grupoId: string): Promise<UsarGrupoOpcionesResponse> {
  return await apiFetch<UsarGrupoOpcionesResponse>(`/api/v1/projects/${projectId}/planos/${fileId}/opciones/grupos/${grupoId}/usar`, { method: 'POST' });
}

export async function descartarGrupoOpcionesPlano(projectId: string, fileId: string, grupoId: string): Promise<void> {
  await apiFetch<void>(`/api/v1/projects/${projectId}/planos/${fileId}/opciones/grupos/${grupoId}`, {
    method: 'DELETE',
    acceptStatus: [204] as number[],
  });
}

/**
 * «Rehacer la hoja»: la hoja nueva reemplaza a la del plano en UNA transacción. Si algo falla
 * (409 la hoja cambió o hay IA generándose; 422 una fila no se pudo poner) no cambia nada.
 */
export async function reemplazarHojaPlano(
  projectId: string,
  fileId: string,
  body: ReemplazarHojaPlanoRequest,
): Promise<{ creados: number; conIa: number }> {
  return await apiFetch<{ creados: number; conIa: number }>(`/api/v1/projects/${projectId}/planos/${fileId}/hoja/reemplazar`, {
    method: 'POST',
    body,
  });
}

/** Lo que se pierde al rehacer la hoja del plano (para el aviso antes de armar). */
export async function trabajoEnRiesgoPlano(projectId: string, fileId: string): Promise<TrabajoEnRiesgoPlano> {
  return await apiFetch<TrabajoEnRiesgoPlano>(`/api/v1/projects/${projectId}/planos/${fileId}/hoja/trabajo-en-riesgo`);
}
